// src/services/skillService.js
// API service for user skill management

import config from '../config';

const API_BASE_URL = config.apiBaseUrl;

class SkillService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authorization headers with JWT token
   */
  getAuthHeaders() {
    const token = localStorage.getItem(config.storage.tokenKey) || localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Get auth headers for multipart (no Content-Type — browser sets it with boundary)
   */
  getUploadHeaders() {
    const token = localStorage.getItem(config.storage.tokenKey) || localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  async handleResponse(response) {
    const data = await response.json().catch(() => ({ error: 'Unknown error' }));
    if (!response.ok) {
      const err = new Error(data.error || `HTTP ${response.status}`);
      err.data = data; // attach full response for violations
      throw err;
    }
    return data;
  }

  // ════════════════════════════════════════════════════
  // CRUD
  // ════════════════════════════════════════════════════

  /** List all skills for the current user */
  async listSkills() {
    const response = await fetch(`${this.baseURL}/api/skills`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /** Get a single skill by ID */
  async getSkill(skillId) {
    const response = await fetch(`${this.baseURL}/api/skills/${skillId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /** Create a new skill from typed content */
  async createSkill({ name, content, description = '', is_default = false }) {
    const response = await fetch(`${this.baseURL}/api/skills`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name, content, description, is_default }),
    });
    return this.handleResponse(response);
  }

  /** Update an existing skill */
  async updateSkill(skillId, { name, content, description }) {
    const body = {};
    if (name !== undefined) body.name = name;
    if (content !== undefined) body.content = content;
    if (description !== undefined) body.description = description;

    const response = await fetch(`${this.baseURL}/api/skills/${skillId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return this.handleResponse(response);
  }

  /** Delete a skill */
  async deleteSkill(skillId) {
    const response = await fetch(`${this.baseURL}/api/skills/${skillId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ════════════════════════════════════════════════════
  // DEFAULT SKILL
  // ════════════════════════════════════════════════════

  /** Set a skill as the user's default */
  async setDefault(skillId) {
    const response = await fetch(`${this.baseURL}/api/skills/${skillId}/default`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /** Clear default skill */
  async clearDefault() {
    const response = await fetch(`${this.baseURL}/api/skills/default`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  // ════════════════════════════════════════════════════
  // VALIDATION
  // ════════════════════════════════════════════════════

  /** Validate skill content without saving (for real-time feedback) */
  async validateContent(content) {
    const response = await fetch(`${this.baseURL}/api/skills/validate`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    return this.handleResponse(response);
  }

  // ════════════════════════════════════════════════════
  // FILE UPLOAD
  // ════════════════════════════════════════════════════

  /** Upload a .txt or .md file as a skill */
  async uploadSkill(file, { name = '', description = '', is_default = false } = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    formData.append('is_default', is_default ? 'true' : 'false');

    const response = await fetch(`${this.baseURL}/api/skills/upload`, {
      method: 'POST',
      headers: this.getUploadHeaders(), // no Content-Type — browser sets multipart boundary
      body: formData,
    });
    return this.handleResponse(response);
  }

  // ════════════════════════════════════════════════════
  // CLIENT-SIDE VALIDATION (mirrors backend patterns)
  // ════════════════════════════════════════════════════

  /**
   * Validate skill content on the client side.
   * Returns { valid: boolean, violations: Array<{reason, match, position}> }
   *
   * This mirrors the backend BLOCKED_PATTERNS so the UI can give
   * instant feedback without a server round-trip.
   */
  validateContentLocally(content) {
    if (!content || !content.trim()) {
      return { valid: false, violations: [{ reason: 'Content is empty', match: '', position: 0 }] };
    }

    const MAX_LENGTH = 5000;
    if (content.length > MAX_LENGTH) {
      return {
        valid: false,
        violations: [{ reason: `Content exceeds maximum length of ${MAX_LENGTH} characters`, match: '', position: 0 }],
      };
    }

    const BLOCKED = [
      // SQL DDL / DML
      { re: /\b(CREATE\s+TABLE|DROP\s+TABLE|ALTER\s+TABLE|TRUNCATE\s+TABLE)\b/gi, reason: 'SQL DDL statement' },
      { re: /\b(INSERT\s+INTO|DELETE\s+FROM|UPDATE\s+\w+\s+SET|MERGE\s+INTO)\b/gi, reason: 'SQL DML statement' },
      { re: /\b(GRANT\s+|REVOKE\s+|EXEC\s+|EXECUTE\s+)\b/gi, reason: 'SQL admin command' },

      // Table/schema references
      { re: /\bTXN_INVOICE_SR_SALESBOT_FINAL\b/gi, reason: 'Direct table name reference' },
      { re: /\bAI_SALES_BOT\.SECONDARY_SALES\b/gi, reason: 'Direct schema reference' },
      { re: /\bUSERSMANAGER\b/gi, reason: 'System table reference' },
      { re: /\bQUESTION_HISTORY_RAW\b/gi, reason: 'System table reference' },
      { re: /\bPREDEFINED_QUESTIONS\b/gi, reason: 'System table reference' },
      { re: /\bUSER_LOCATION_MAP\b/gi, reason: 'System table reference' },

      // Column names from semantic model
      { re: /\bINV_DT\b/g, reason: 'Column name from semantic model' },
      { re: /\bSO_DT\b/g, reason: 'Column name from semantic model' },
      { re: /\b"VOL\(Phy\/Cs\)"\b/g, reason: 'Column name from semantic model' },
      { re: /\bDIST_CD\b/g, reason: 'Column name from semantic model' },
      { re: /\bSLSMAN_CD\b/g, reason: 'Column name from semantic model' },
      { re: /\bCUST_CD\b/g, reason: 'Column name from semantic model' },
      { re: /\bREGION_CD\b/g, reason: 'Column name from semantic model' },
      { re: /\bTXN_TYPE\b/g, reason: 'Column name from semantic model' },
      { re: /\bINV_NO\b/g, reason: 'Column name from semantic model' },
      { re: /\bINV_KEY\b/g, reason: 'Column name from semantic model' },
      { re: /\bMATERIAL_TYPE\b/g, reason: 'Column name from semantic model' },

      // Prompt injection
      { re: /ignore\s+(previous|prior|above|all)\s+(instructions?|prompts?|rules?)/gi, reason: 'Prompt injection attempt' },
      { re: /disregard\s+(previous|prior|above|all)\s+(instructions?|prompts?|rules?)/gi, reason: 'Prompt injection attempt' },
      { re: /forget\s+(everything|all|your)\s+(instructions?|rules?|training)/gi, reason: 'Prompt injection attempt' },
      { re: /you\s+are\s+now\s+a\b/gi, reason: 'Role override attempt' },
      { re: /act\s+as\s+if\s+you\s+are\b/gi, reason: 'Role override attempt' },
      { re: /pretend\s+(to\s+be|you\s+are)\b/gi, reason: 'Role override attempt' },
      { re: /system\s*:\s*/gi, reason: 'System prompt injection' },
    ];

    const violations = [];
    for (const { re, reason } of BLOCKED) {
      // Reset lastIndex for global regexes
      re.lastIndex = 0;
      let match;
      while ((match = re.exec(content)) !== null) {
        violations.push({
          reason,
          match: match[0],
          position: match.index,
        });
      }
    }

    return { valid: violations.length === 0, violations };
  }

  /**
   * Validate file extension on the client side.
   */
  validateFileExtension(filename) {
    if (!filename) return false;
    const ext = filename.includes('.') ? '.' + filename.split('.').pop().toLowerCase() : '';
    return ['.txt', '.md'].includes(ext);
  }
}

export const skillService = new SkillService();
export default skillService;
